import { Button, ClassName, Label, Row, Theme } from "@busybusy/webapp-react-ui";
import classNames from "classnames";
import ConditionalContainer from "components/foundation/ConditionalContainer/ConditionalContainer";
import useSlackEquipmentTrackingAccess from "containers/settings/integrations/vendors/slack/hooks/useSlackEquipmentTrackingAccess";
import { useToastDispatch } from "contexts/ToastContext";
import useOrganizationUiSettingsUpdate from "hooks/models/organization/settings/hooks/useOrganizationUiSettingsUpdate";
import { t } from "i18next";
import { FunctionComponent, useState } from "react";

export interface IEquipmentTrackingEnableFormProps {
  onRequestSubmitted: () => void;
  className?: ClassName;
}

const EquipmentTrackingEnableForm: FunctionComponent<IEquipmentTrackingEnableFormProps> = (props) => {
  const { className, onRequestSubmitted } = props;
  const toastDispatch = useToastDispatch();
  const [showErrorTip, setShowErrorTip] = useState(false);
  const equipmentTrackingAccess = useSlackEquipmentTrackingAccess();
  const updateUiSettings = useOrganizationUiSettingsUpdate();

  const classes = classNames('equipment-tracking-enable-form', className);

  const onLinkClicked = () => {
    window.open('https://busybusy.wistia.com/medias/w3xxhi7xiv');
  };

  const requestAccess = async () => {
    let requestSuccessful = false;
    try {
      const response = await equipmentTrackingAccess();
      requestSuccessful = response.status === 200;
    } catch (e) {
      requestSuccessful = false;
    }
    if (requestSuccessful) {
      updateUiSettings('requestedEquipmentTrackingAccess', true);
      onRequestSubmitted();
    } else {
      toastDispatch({
        type: 'OPEN',
        payload: { label: t('Failed to send request. Try again later.'), theme: Theme.DANGER },
      });
      setShowErrorTip(true);
    }
  };

  return (
    <div className={classes}>
      <Label className="mt-3 mb-2">{t('With GPS Equipment Tracking, you can:')}</Label>
      <ul className="pl-4">
        <li>{t('View tagged equipment on a universal map, along with clocked-in employees & projects')}</li>
        <li>{t('See movement history and breadcrumbing')}</li>
        <li>
          {t('Access telematics data like engine status, run time, idle time, RPM, and fuel level')}
        </li>
      </ul>
      {t('All GPS features are available on both Web and Mobile, so your team can stay connected from the office or the field.')}
      <Label className="mt-3 mb-2">{t('Flexible Tracking')}</Label>
      {t('Not every piece of equipment needs the same level of tracking. Choose the right visibility for each asset — from a simple GPS location ping to deeper telematics. We even offer a Dashcam option. \n')}
      {t('Start with what matters most and expand as your needs grow.')}
      <Label className="mt-7 mb-2">{t('Learn More')}</Label>
      <div>{t('Want a deeper look?')}</div>
      <div>
        {t('→ ')}
        <a className="link ml-1" onClick={onLinkClicked}>
          {t('Explore the Gps Equipment Tracking video.')}
        </a>
      </div>
      <Label className="mt-7 mb-2">{t('How to Get Started')}</Label>
      <div className="content">
        {t(
          `Click the button below, and a BusyBusy team member will connect with you to walk through GPS Equipment Tracking and answer your questions.`
        )}
      </div>
      <Label className="mt-7 mb-2">{t('Pricing')}</Label>
      <div className="content">{t('Speak with our sales team for full pricing details.')}</div>
      <Row className="mt-9">
        <Button type="primary" onClick={requestAccess}>
          {t(`Yes - I'm Interested`)}
        </Button>
      </Row>

      <ConditionalContainer condition={showErrorTip}>
        <br />
        {t('* If you have an ad blocker, disable it and try again.')}
      </ConditionalContainer>
    </div>
  )
}

export default EquipmentTrackingEnableForm;